import { useState, useCallback, useMemo } from 'react';
import { subDays } from 'date-fns';
import { toLocalDateStr } from '../lib/utils';
import { createNotionService } from '@/services/notion-service';
import { NotionIndicators, type NotionActivityData, type NotionEventItem } from './NotionIndicators';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faNotion } from '@fortawesome/free-brands-svg-icons';
import {
  faArrowLeft, faArrowTrendUp, faCalendarDays, faChevronDown, faChevronRight,
  faCircleExclamation, faExternalLinkAlt, faFileLines, faGear,
  faMagnifyingGlass, faRotate, faSpinner, faUsers,
} from '@fortawesome/free-solid-svg-icons';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell,
} from 'recharts';

interface Props {
  inline?: boolean;
  onClose: () => void;
  onOpenSettings?: () => void;
  reviewData?: any;
}

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#a855f7', '#f43f5e', '#06b6d4'];

function parentLabel(t?: string) {
  if (!t || t === 'workspace') return 'Workspace';
  if (t === 'database_id') return 'Database';
  if (t === 'page_id') return 'Sub-page';
  return t;
}

export function NotionActivityView({ onClose, inline, onOpenSettings }: Props) {
  const [isDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [customFrom, setCustomFrom] = useState(() => toLocalDateStr(subDays(new Date(), 30)));
  const [customTo, setCustomTo] = useState(() => toLocalDateStr());
  const [datePreset, setDatePreset] = useState(30);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [data, setData] = useState<NotionActivityData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCharts, setShowCharts] = useState(true);
  const [filterAuthor, setFilterAuthor] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'new' | 'edited'>('all');
  const [indicatorTrigger, setIndicatorTrigger] = useState(0);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());

  const ts = useMemo(() => ({
    backgroundColor: isDark ? '#1e293b' : '#fff',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`,
    borderRadius: '8px', fontSize: '12px', color: isDark ? '#fff' : '#111',
  }), [isDark]);

  const lookbackDays = Math.max(1, Math.ceil(
    (new Date(customTo).getTime() - new Date(customFrom).getTime()) / 86_400_000
  ));

  const buildData = useCallback((pages: any[]): NotionActivityData => {
    const events: NotionEventItem[] = pages.map(p => {
      const isNew = p.createdTime && p.lastEditedTime &&
        new Date(p.lastEditedTime).getTime() - new Date(p.createdTime).getTime() < 10_000;
      return {
        id: p.id, title: p.title || 'Untitled', url: p.url,
        isNew: !!isNew, createdTime: p.createdTime, lastEditedTime: p.lastEditedTime,
        createdById: p.createdBy?.id ?? '', createdByName: p.createdBy?.name ?? 'Unknown',
        editedById: p.lastEditedBy?.id ?? '', editedByName: p.lastEditedBy?.name ?? 'Unknown',
        parentType: p.parent?.type ?? p.parentType, timestamp: p.lastEditedTime,
      };
    });
    const dayMap = new Map<string, { date: string; _ts: number; created: number; edited: number }>();
    for (const e of events) {
      const key = toLocalDateStr(new Date(e.timestamp));
      if (!dayMap.has(key)) dayMap.set(key, { date: new Date(key).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' }), _ts: new Date(key).getTime(), created: 0, edited: 0 });
      const s = dayMap.get(key)!;
      if (e.isNew) s.created++; else s.edited++;
    }
    const dailyCounts = Array.from(dayMap.values()).sort((a, b) => a._ts - b._ts).map(({ _ts: _, ...r }) => r);
    const authorMap = new Map<string, { id: string; name: string; count: number }>();
    for (const e of events) {
      const k = e.editedById || e.editedByName;
      if (!authorMap.has(k)) authorMap.set(k, { id: e.editedById, name: e.editedByName, count: 0 });
      authorMap.get(k)!.count++;
    }
    const authors = Array.from(authorMap.values()).sort((a, b) => b.count - a.count);
    return { events, dailyCounts, authors, totalPages: pages.length, uniqueContributors: authors.length, dateRange: { start: customFrom, end: customTo } };
  }, [customFrom, customTo]);

  const fetchActivity = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const svc = await createNotionService();
      if (!svc) throw new Error('NOT_CONNECTED');
      const { events: pages } = await svc.getEvents(new Date(customFrom), new Date(customTo + 'T23:59:59'));
      const newData = buildData(pages);
      setData(newData);
      setIndicatorTrigger(t => t + 1);
      setExpandedDates(new Set([toLocalDateStr(), toLocalDateStr(subDays(new Date(), 1))]));
    } catch (e: any) { setError(e.message ?? 'Failed to fetch Notion activity'); }
    finally { setLoading(false); }
  }, [customFrom, customTo, buildData]);

  const filteredEvents = useMemo(() => {
    if (!data) return [];
    return data.events.filter(e => {
      if (filterAuthor && e.editedByName !== filterAuthor) return false;
      if (filterType === 'new' && !e.isNew) return false;
      if (filterType === 'edited' && e.isNew) return false;
      if (filterSearch && !e.title.toLowerCase().includes(filterSearch.toLowerCase()) && !e.editedByName.toLowerCase().includes(filterSearch.toLowerCase())) return false;
      return true;
    });
  }, [data, filterAuthor, filterSearch, filterType]);

  const groupedByDate = useMemo(() => {
    const groups = new Map<string, NotionEventItem[]>();
    for (const e of filteredEvents) {
      const key = toLocalDateStr(new Date(e.timestamp));
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(e);
    }
    return Array.from(groups.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filteredEvents]);

  const toggleDate = (d: string) => setExpandedDates(prev => { const n = new Set(prev); n.has(d) ? n.delete(d) : n.add(d); return n; });
  const barData = useMemo(() => data?.authors.map(a => ({ name: a.name.split(' ')[0], fullName: a.name, pages: a.count })) ?? [], [data]);
  const pieData = useMemo(() => !data ? [] : [
    { name: 'New', value: data.events.filter(e => e.isNew).length, fill: '#10b981' },
    { name: 'Edited', value: data.events.filter(e => !e.isNew).length, fill: '#3b82f6' },
  ].filter(d => d.value > 0), [data]);
  const parentTypeData = useMemo(() => {
    if (!data) return [];
    const m = new Map<string, number>();
    data.events.forEach(e => { const t = parentLabel(e.parentType); m.set(t, (m.get(t) ?? 0) + 1); });
    return Array.from(m.entries()).map(([name, value], i) => ({ name, value, fill: COLORS[i % COLORS.length] }));
  }, [data]);

  const wrap = inline ? 'h-full overflow-y-auto' : 'fixed inset-0 z-[100] bg-white dark:bg-slate-900 overflow-y-auto';

  return (
    <div className={wrap}>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-gray-200 dark:border-slate-700/50">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {!inline && (
              <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                <FontAwesomeIcon icon={faArrowLeft} className="h-5 w-5 text-gray-500 dark:text-slate-400" />
              </button>
            )}
            <FontAwesomeIcon icon={faNotion} className="h-5 w-5 text-gray-900 dark:text-white" />
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Notion Activity</h1>
          </div>
          {data && <span className="text-xs text-gray-400 dark:text-slate-500">{filteredEvents.length} events</span>}
        </div>
      </div>

      <div className="px-6 py-6 space-y-6">
        {/* Config panel */}
        <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl p-5">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-4">
              <div className="flex items-center justify-between mb-2">
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wide">
                  <FontAwesomeIcon icon={faCalendarDays} className="h-3.5 w-3.5" /> Time Range
                </label>
                <button onClick={() => setShowDatePicker(v => !v)} className={`text-[10px] px-2 py-0.5 rounded ${showDatePicker ? 'bg-cyan-500/20 text-cyan-400' : 'text-gray-500 dark:text-slate-500'}`}>
                  {showDatePicker ? 'Hide' : 'Custom'}
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-1">
                {[7, 14, 30, 60, 90, 180].map(days => (
                  <button key={days} onClick={() => { setDatePreset(days); setCustomTo(toLocalDateStr()); setCustomFrom(toLocalDateStr(subDays(new Date(), days))); setShowDatePicker(false); }}
                    className={`px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${datePreset === days && !showDatePicker ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' : 'bg-gray-100 dark:bg-slate-700/30 text-gray-500 dark:text-slate-400 border-transparent hover:text-gray-900 dark:hover:text-white'}`}>
                    {days === 7 ? '7d' : days === 14 ? '2w' : days === 30 ? '30d' : days === 60 ? '60d' : days === 90 ? '90d' : '6mo'}
                  </button>
                ))}
              </div>
              {showDatePicker && (
                <div className="flex gap-2 items-center mt-1">
                  <input type="date" value={customFrom} max={customTo} onChange={e => { setCustomFrom(e.target.value); setDatePreset(0); }} className="flex-1 px-2.5 py-1.5 text-xs bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white focus:outline-none" />
                  <span className="text-xs text-gray-400">to</span>
                  <input type="date" value={customTo} min={customFrom} max={toLocalDateStr()} onChange={e => { setCustomTo(e.target.value); setDatePreset(0); }} className="flex-1 px-2.5 py-1.5 text-xs bg-gray-100 dark:bg-slate-700/30 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-white focus:outline-none" />
                </div>
              )}
              <div className="text-[10px] text-slate-500 mt-1">{lookbackDays} days</div>
            </div>
            <div className="md:col-span-2 flex items-end">
              <button onClick={fetchActivity} disabled={loading}
                className="w-full px-4 py-2.5 bg-gray-900 hover:bg-gray-700 dark:bg-white dark:hover:bg-gray-200 text-white dark:text-gray-900 text-sm font-medium rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 transition-all">
                {loading ? <><FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> Loading…</> : <><FontAwesomeIcon icon={faRotate} className="h-4 w-4" /> Fetch</>}
              </button>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (error === 'NOT_CONNECTED' ? (
          <div className="flex flex-col items-center gap-3 p-6 bg-amber-500/10 border border-amber-500/20 rounded-xl text-center">
            <FontAwesomeIcon icon={faCircleExclamation} className="h-6 w-6 text-amber-400" />
            <div>
              <p className="text-sm font-medium text-amber-700 dark:text-amber-300">Not connected to Notion</p>
              <p className="text-xs text-amber-600/80 dark:text-amber-400/70 mt-1">Add your Notion Internal Integration Token in Settings → Notion.</p>
            </div>
            <button onClick={() => onOpenSettings?.()} className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white text-sm font-medium rounded-lg transition-colors">
              <FontAwesomeIcon icon={faGear} className="h-3.5 w-3.5" /> Open Settings
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
            <FontAwesomeIcon icon={faCircleExclamation} className="h-5 w-5 text-red-400 flex-shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        ))}

        {/* Indicators */}
        {data && <NotionIndicators data={data} triggerCalculate={indicatorTrigger} lookbackDays={lookbackDays} />}

        {/* Charts */}
        {data && (
          <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
            <button onClick={() => setShowCharts(v => !v)} className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/20 transition-colors">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faArrowTrendUp} className="h-4 w-4 text-emerald-500" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">Charts &amp; Analytics</span>
                <span className="text-[10px] text-gray-400 dark:text-slate-500">({data.events.length} events, {data.uniqueContributors} contributors)</span>
              </div>
              <FontAwesomeIcon icon={faChevronDown} className={`h-4 w-4 text-slate-400 transition-transform ${showCharts ? 'rotate-180' : ''}`} />
            </button>
            {showCharts && (
              <div className="px-5 pb-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl border border-gray-200 dark:border-slate-700/30 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faArrowTrendUp} className="h-3.5 w-3.5 text-emerald-400" />
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Daily Activity</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={170} minWidth={0}>
                    <AreaChart data={data.dailyCounts}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                      <YAxis tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} allowDecimals={false} />
                      <Tooltip contentStyle={ts} />
                      <Area type="monotone" dataKey="edited" name="Edited" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
                      <Area type="monotone" dataKey="created" name="Created" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl border border-gray-200 dark:border-slate-700/30 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faUsers} className="h-3.5 w-3.5 text-blue-400" />
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Top Contributors</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={170} minWidth={0}>
                    <BarChart data={barData.slice(0, 8)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                      <XAxis type="number" tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} width={60} />
                      <Tooltip contentStyle={ts} formatter={(v, _, p) => [v, p.payload.fullName]} />
                      <Bar dataKey="pages" name="Pages" fill="#3b82f6" radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-gray-50 dark:bg-slate-900/50 rounded-xl border border-gray-200 dark:border-slate-700/30 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faFileLines} className="h-3.5 w-3.5 text-violet-400" />
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white">New vs Edited</h3>
                  </div>
                  <div className="flex items-center justify-center gap-6">
                    <ResponsiveContainer width="55%" height={140} minWidth={0}>
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={28} outerRadius={55} paddingAngle={2} dataKey="value">
                          {pieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                        </Pie>
                        <Tooltip contentStyle={ts} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-2">
                      {pieData.map((d, i) => (
                        <div key={i} className="flex items-center gap-2 text-[11px]">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.fill }} />
                          <span className="text-gray-600 dark:text-slate-400">{d.name}</span>
                          <span className="font-semibold text-gray-900 dark:text-white ml-1">{d.value}</span>
                        </div>
                      ))}
                      <div className="pt-1 border-t border-gray-200 dark:border-slate-700/30">
                        {parentTypeData.map((d, i) => (
                          <div key={i} className="flex items-center gap-2 text-[10px]">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.fill }} />
                            <span className="text-gray-500 dark:text-slate-500">{d.name}: {d.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        {data && (
          <div className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl p-4 flex flex-wrap gap-3">
            <div className="flex items-center gap-2 flex-1 min-w-[160px]">
              <FontAwesomeIcon icon={faMagnifyingGlass} className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              <input value={filterSearch} onChange={e => setFilterSearch(e.target.value)} placeholder="Search pages…"
                className="flex-1 text-xs bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-400" />
            </div>
            <select value={filterAuthor} onChange={e => setFilterAuthor(e.target.value)}
              className="text-xs px-2 py-1.5 bg-gray-100 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600 rounded-lg text-gray-700 dark:text-slate-300">
              <option value="">All authors</option>
              {data.authors.map(a => <option key={a.id} value={a.name}>{a.name} ({a.count})</option>)}
            </select>
            <div className="flex items-center gap-1">
              {(['all', 'new', 'edited'] as const).map(t => (
                <button key={t} onClick={() => setFilterType(t)}
                  className={`px-2.5 py-1.5 text-xs rounded-lg border transition-colors ${filterType === t ? 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400 border-cyan-500/30' : 'bg-gray-100 dark:bg-slate-700/30 text-gray-500 dark:text-slate-400 border-transparent'}`}>
                  {t === 'all' ? 'All' : t === 'new' ? 'New' : 'Edited'}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Event feed */}
        {data && groupedByDate.length === 0 && (
          <div className="text-center py-12 text-gray-400 dark:text-slate-500 text-sm">No events match your filters</div>
        )}
        {groupedByDate.map(([dateKey, evts]) => {
          const isOpen = expandedDates.has(dateKey);
          const label = new Date(dateKey).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
          return (
            <div key={dateKey} className="bg-white/90 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/50 rounded-xl overflow-hidden">
              <button onClick={() => toggleDate(dateKey)} className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/20 transition-colors">
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} className="h-3.5 w-3.5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{label}</span>
                  <span className="text-xs text-gray-400 dark:text-slate-500">{evts.length} page{evts.length !== 1 ? 's' : ''}</span>
                </div>
              </button>
              {isOpen && (
                <div className="divide-y divide-gray-100 dark:divide-slate-700/30">
                  {evts.map(e => (
                    <div key={e.id} className="px-5 py-3 flex items-start justify-between gap-3 hover:bg-gray-50 dark:hover:bg-slate-700/10">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <span className={`mt-0.5 flex-shrink-0 inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${e.isNew ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' : 'bg-blue-500/15 text-blue-700 dark:text-blue-400'}`}>
                          {e.isNew ? 'New' : 'Edit'}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{e.title}</p>
                          <p className="text-xs text-gray-500 dark:text-slate-400">
                            {e.editedByName}{e.parentType ? ` · ${parentLabel(e.parentType)}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-[10px] text-gray-400 dark:text-slate-500">
                          {new Date(e.timestamp).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {e.url && (
                          <a href={e.url} target="_blank" rel="noopener noreferrer"
                            className="text-gray-400 hover:text-blue-500 transition-colors" title="Open in Notion">
                            <FontAwesomeIcon icon={faExternalLinkAlt} className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {!data && !loading && !error && (
          <div className="text-center py-16 text-gray-400 dark:text-slate-500">
            <FontAwesomeIcon icon={faNotion} className="h-10 w-10 mb-3 opacity-30" />
            <p className="text-sm">Select a time range and click <strong>Fetch</strong> to load Notion activity</p>
          </div>
        )}
      </div>
    </div>
  );
}
